<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	variant: $.select(
		'Header Type',
		[
			{ label: 'Series', value: 'series' },
			{ label: 'Symposium', value: 'symposium' },
		],
		{ default: 'series' },
	),
})
export const SeriesHeader = defineBlock({
	thumbnail: '/block-thumbs/SeriesHeader.svg',
	category: 'Hero',
	order: 330,
	name: 'SeriesHeader',
	label: 'Series Header',
	icon: 'panorama',
	schema,
})
</script>
<script setup lang="ts">

import { Btn, Image } from '@bagelink/vue'
import { computed, ref } from 'vue'
import ArticleActions from '@/components/blocks/ArticleActions.vue'
import { useBoundPageModel } from '@/composables/useBoundPageItem'
import { ensureAuthors, resolveAuthorForBlock } from '@/composables/useDatastore'
import { displayImage } from '@/utils/image'



const props = defineProps<{
	variant?: 'series' | 'symposium'
}>()

ensureAuthors()

const previewRecord = {
	title: 'A Sample Series',
	teaser: '<p>Exploring a central theme through a collection of articles</p>',
	image: '/og.jpg',
	imageCaption: '<p>Sample series image caption</p>',
	introductionTitle: 'Introduction',
	content: '<p>This introduction previews how a bound series record will appear.</p>',
	author: {
		name: 'Sample Author',
		title: 'Professor',
		image: '/webclip.png',
		slug: 'sample-author',
	},
}

function normalizeSeries(record: Record<string, any>) {
	const resolvedAuthor = resolveAuthorForBlock(record.authorId)
	const author = resolvedAuthor || record.author || null
	return {
		title: record.title || record.name || '',
		teaser: record.teaser || record.metaTitle || '',
		image: record.image || record.headerImage || '',
		imageCaption: record.imageCaption || record.caption || '',
		introductionTitle: record.introductionTitle || 'Introduction',
		introduction: record.content || record.introduction || '',
		authorName: author?.name || '',
		authorTitle: author?.title || '',
		authorImage: author?.image || '',
		authorLink: author?.slug ? `/author/${author.slug}` : '',
	}
}

const page = useBoundPageModel(normalizeSeries, previewRecord)
const pageTitle = computed(() => page.value?.title || '')
const pageTeaser = computed(() => page.value?.teaser || '')
const pageImage = computed(() => page.value?.image || '')
const pageImageCaption = computed(() => page.value?.imageCaption || '')
const pageIntroductionTitle = computed(() => page.value?.introductionTitle || 'Introduction')
const pageIntroduction = computed(() => page.value?.introduction || '')
const pageAuthorName = computed(() => page.value?.authorName || '')
const pageAuthorTitle = computed(() => page.value?.authorTitle || '')
const pageAuthorImage = computed(() => page.value?.authorImage || '')
const pageAuthorLink = computed(() => page.value?.authorLink || '')
const readMore = ref(false)
</script>

<template>
	<div class="w1100px py-1 m_w-100p m_pt-0">
		<figure class="m-0">
			<div class="relative h-450px px-1 m_h-350px basicPrint">
				<div class="relative z-2 color-white mx-auto txt-center py-2 h100p flex column">
					<div class="mt-auto">
						<div v-if="pageTeaser" class="flex justify-content-center gap-1 pb-05">
							<div class="dashline-white px-1" />
							<div class="txt17 light m_txt12 white-space-nowrap" v-html="pageTeaser" />
							<div class="dashline-white px-1" />
						</div>
					</div>
					<h1 class="txt28 regular m-0 line-height-12 balance m_txt20 m_line-height-13">
						{{ pageTitle }}
					</h1>
				</div>
				<div class="absolute z-1 gradient inset-0 w-100p h-100p" />
				<Image
					v-if="pageImage"
					:src="displayImage(pageImage, 'hero')"
					:alt="pageTitle"
					class="absolute inset-0 cover w-100p h-100p z-0"
				/>
			</div>
			<figcaption
				v-if="pageImageCaption"
				class="txt13 light txt-center line-height-17 px-1 pt-025"
				v-html="pageImageCaption"
			/>
		</figure>

		<div
			v-if="props.variant !== 'symposium' && pageAuthorName"
			class="flex py-1 m_px-1 space-between align-items-center m_column-reverse m_gap-2"
		>
			<router-link :to="pageAuthorLink" class="flex gap-1 decoration-none hover">
				<Image
					:src="displayImage(pageAuthorImage, 'thumb')"
					:alt="pageAuthorName"
					ratio="1"
					class="w60px h60px round cover hidePrint"
					lazy
				/>
				<div>
					<p class="txt20">
						{{ pageAuthorTitle }}
						{{ pageAuthorName }}
					</p>
				</div>
			</router-link>
			<ArticleActions class="hidePrint" />
		</div>
		<div v-else class="flex p-1 w720px mx-auto m_px-1 hidePrint">
			<ArticleActions class="ms-auto m_mx-auto hidePrint" />
		</div>

		<div
			v-if="pageIntroduction"
			class="introduction-section w720px mx-auto m_px-1 pt-2 m_pt-1"
			:class="{ maskReadMore: !readMore && pageIntroduction.length > 100 }"
		>
			<h2
				v-if="props.variant !== 'symposium'"
				class="txt25 mx-0 mt-0 mb-1 txt-center regular line-height-13 balance"
			>
				{{ pageIntroductionTitle }}
			</h2>
			<div class="txt17 m_txt15 sym-rtf pretty" v-html="pageIntroduction" />
		</div>

		<div v-if="!readMore && pageIntroduction.length > 100" class="txt-center pt-1 hidePrint">
			<Btn class="txt17 color-red" flat @click="readMore = !readMore"> Read More </Btn>
		</div>
	</div>
</template>
